import { getLevelInfo } from '../../utils/scoring';
import { GeometricAvatar } from '../shared/GeometricAvatar';

export const ViewProfileModal = ({
  showProfile,
  viewingProfile,
  setShowProfile,
  setViewingProfile,
  formatTimestamp,
}) => {
  if (!showProfile || !viewingProfile) return null;

  return (
    <div className="fixed inset-0 z-[250] bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 to-black border-4 border-purple-500 p-6 sm:p-8 max-w-2xl w-full my-8 sm:my-0">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <GeometricAvatar username={viewingProfile.username} size={64} />
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-purple-400 uppercase break-words">{viewingProfile.username}</h2>
              {viewingProfile.bio && (
                <p className="text-gray-300 text-sm font-bold italic mt-1">"{viewingProfile.bio}"</p>
              )}
              <p className="text-gray-500 text-sm font-bold mt-1">Member since {new Date(viewingProfile.memberSince).toLocaleDateString()}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setShowProfile(false);
              setViewingProfile(null);
            }}
            className="text-gray-500 hover:text-white font-black text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-black border-2 border-lime-500 p-3 text-center">
            <div className="text-2xl font-black text-lime-500">{viewingProfile.stats.streak}</div>
            <div className="text-xs font-black text-lime-400 uppercase">Streak</div>
          </div>
          <div className="bg-black border-2 border-orange-500 p-3 text-center">
            <div className="text-2xl font-black text-orange-500">{viewingProfile.stats.totalSends}</div>
            <div className="text-xs font-black text-orange-400 uppercase">Total Sends</div>
          </div>
          <div className="bg-black border-2 border-purple-500 p-3 text-center">
            <div className="text-2xl font-black text-purple-500">{viewingProfile.stats.longestStreak}</div>
            <div className="text-xs font-black text-purple-400 uppercase">Best Streak</div>
          </div>
          <div className="bg-black border-2 border-yellow-500 p-3 text-center">
            <div className="text-2xl font-black text-yellow-500">{viewingProfile.badges?.length || 0}</div>
            <div className="text-xs font-black text-yellow-400 uppercase">Badges</div>
          </div>
        </div>

        {/* Level Badge */}
        <div className={`bg-gradient-to-r ${getLevelInfo(viewingProfile.stats.totalSends * 5).currentLevel.bg} border-3 border-black p-4 mb-6 text-center`}>
          <p className="text-white/60 text-xs font-black uppercase mb-1">Current Rank</p>
          <p className={`text-3xl font-black ${getLevelInfo(viewingProfile.stats.totalSends * 5).currentLevel.color} uppercase`}>
            {getLevelInfo(viewingProfile.stats.totalSends * 5).currentLevel.title}
          </p>
        </div>

        {/* Category Breakdown */}
        {viewingProfile.categoryBreakdown && viewingProfile.categoryBreakdown.length > 0 && (
          <div className="bg-black border-2 border-gray-700 p-4">
            <h3 className="text-lg font-black text-gray-400 uppercase mb-3">Top Categories</h3>
            <div className="space-y-3">
              {viewingProfile.categoryBreakdown.slice(0, 3).map((cat) => {
                const maxCount = viewingProfile.categoryBreakdown[0].count;
                const percentage = (cat.count / maxCount) * 100;
                return (
                  <div key={cat.category}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-black text-white uppercase">{cat.category}</span>
                      <span className="text-sm font-black text-lime-400">{cat.count} sends</span>
                    </div>
                    <div className="w-full bg-gray-800 h-2">
                      <div
                        className="bg-gradient-to-r from-lime-500 to-green-600 h-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {viewingProfile.recentSends && viewingProfile.recentSends.length > 0 && (
          <div className="bg-black border-2 border-gray-700 p-4 mt-4">
            <h3 className="text-lg font-black text-gray-400 uppercase mb-3">Recent Sends</h3>
            <div className="space-y-2">
              {viewingProfile.recentSends.map((send, i) => (
                <div key={i} className="p-2 bg-gray-900 border border-gray-800">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-black px-2 py-0.5 bg-purple-600 text-white uppercase">
                      {send.category}
                    </span>
                    {send.isWeekly && (
                      <span className="text-xs font-black px-2 py-0.5 bg-yellow-600 text-black">
                        WEEKLY
                      </span>
                    )}
                    {send.isCustom && (
                      <span className="text-xs font-black px-2 py-0.5 bg-indigo-600 text-white">
                        CUSTOM
                      </span>
                    )}
                  </div>
                  <p className="text-white text-xs font-bold line-clamp-2">{send.challenge}</p>
                  <p className="text-gray-500 text-xs font-bold mt-1">
                    {formatTimestamp(send.timestamp)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
